import React, { useEffect, useRef, useState } from 'react';
import PolButton from "v4/components/ui/PolButton/PolButton";
import { useProspectPageContext } from "v4/contexts/ProspectPageContext";
import { CORE_PENNYLANE_QUOTE_SYNC } from "v4/data/apiRoutes";
import {
    openPennylaneLink,
    PENNYLANE_MODAL_ERROR_STATUSES,
    readModules,
    readPennylane
} from "v4/features/front/pennylane/pennylane.utils";
import useFetch from "v4/hooks/useFetch";
import useNotification from "v4/hooks/useNotification";
import { usePolTranslation } from "v4/hooks/usePolTranslation";
import { generateUrl } from "v4/services/api.service";

export default function usePennylaneSync(quote) {
    const { t } = usePolTranslation();
    const { setQuote } = useProspectPageContext();
    const { addSuccessNotification, addErrorNotification } = useNotification();
    const [{ data, isLoading, rawResponse, errorData }, fetchSync] = useFetch('json', { ignoreErrorMessages: true });
    const [error, setError] = useState(null);
    const lastRequestRef = useRef(null);
    const lastHandledResponse = useRef(null);

    const run = (config) => {
        const quoteId = quote?.metadata?.id;
        if (!quoteId) return;

        const request = { url: generateUrl(CORE_PENNYLANE_QUOTE_SYNC, { id: quoteId }), config };
        lastRequestRef.current = request;
        setError(null);
        fetchSync(request);
    };

    const sync = () => run({ method: 'POST' });

    const retry = () => {
        const request = lastRequestRef.current;
        if (request) run(request.config);
    };

    const clearError = () => setError(null);

    // `quote`, `setQuote`, `t` et les notifications sont volontairement hors deps :
    // l'effet ne doit rejouer que sur une nouvelle réponse, pas sur un re-render du devis.
    useEffect(() => {
        if (!rawResponse) return;
        if (lastHandledResponse.current === rawResponse) return;
        lastHandledResponse.current = rawResponse;

        const status = rawResponse.status;

        if (status === 201 || status === 200) {
            setError(null);

            if (data?.data) {
                setQuote(mergeSyncData(quote, data.data));
            }

            const defaultMessage = status === 201
                ? t('pennylane_create_success')
                : t('pennylane_update_success');
            const quoteUrl = data?.data?.quote?.url ?? readPennylane(quote)?.quote?.url ?? null;

            addSuccessNotification(
                <>
                    <p>
                        {data?.details ?? defaultMessage}
                    </p>
                    {quoteUrl && (
                        <PolButton className="alertNotice__action" onClick={() => openPennylaneLink(quoteUrl)}>
                            {t('pennylane_access_quote')}
                        </PolButton>
                    )}
                </>
            );
            return;
        }

        if (status === 403) {
            addErrorNotification(errorData?.details ?? errorData?.title ?? t('pennylane_error_generic'));
            return;
        }

        if (PENNYLANE_MODAL_ERROR_STATUSES.includes(status)) {
            if (status === 409 && errorData?.data) {
                setQuote(mergeConflictData(quote, errorData.data));
            }
            setError({ status, data: errorData });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawResponse, data, errorData]);

    return {
        sync,
        isLoading,
        error,
        clearError,
        retry,
    };
}

function mergeSyncData(quote, syncData) {
    return writePennylane(quote, (pennylane) => ({
        ...pennylane,
        quote: { ...pennylane.quote, url: syncData.quote?.url ?? pennylane.quote?.url ?? null },
    }));
}

function mergeConflictData(quote, conflictData) {
    return writePennylane(quote, (pennylane) => ({
        ...pennylane,
        invoice: { ...pennylane.invoice, url: conflictData.invoice?.url ?? pennylane.invoice?.url ?? null },
        display: { ...pennylane.display, ...conflictData.display },
    }));
}

function writePennylane(quote, build) {
    const rawModules = quote.metadata.modules;
    const modules = readModules(rawModules);

    const newModules = { ...modules, pennylane: build(modules.pennylane ?? {}) };

    return {
        ...quote,
        metadata: {
            ...quote.metadata,
            modules: typeof rawModules === 'string' ? JSON.stringify(newModules) : newModules,
        },
    };
}
